import CGT from "../../utils/CGT.ts";

export const numberFormat = new Intl.NumberFormat();

export type StabilityLevel = "stable" | "unsettled" | "unsteady" | "volatile";
export type StabilityStatus = StabilityLevel | "expired" | "unknown";

const STABILITY_MAX_MONTHS: Record<StabilityLevel, number> = {
  stable: 14,
  unsettled: 6,
  unsteady: 3,
  volatile: 1,
};

const MONTH_IN_DAYS = 30;

const cgtRegex = /Year\s+(\d+)\s+Day\s+(\d+),\s*(\d{1,2}):(\d{2})(?::(\d{2}))?/i;

export function parseCgtToUnix(dateString: string): number {
  const match = dateString.match(cgtRegex);
  if (!match) {
    throw new Error(`Invalid CGT date: ${dateString}`);
  }

  const year = Number(match[1]);
  const day = Number(match[2]);
  const hour = Number(match[3]);
  const minute = Number(match[4]);
  const second = Number(match[5] ?? 0);

  return Math.round(CGT.fromCGT(year, day, hour, minute, second).toSeconds());
}

export function formatCgt(unixSeconds: number): string {
  return CGT.fromSeconds(unixSeconds).toCGT();
}

function normalizeStability(stability?: string | null): StabilityLevel | null {
  if (!stability) {
    return null;
  }
  const lower = stability.toLowerCase();
  if (lower === "stable" || lower === "unsettled" || lower === "unsteady" || lower === "volatile") {
    return lower;
  }
  return null;
}

export function getElapsedMonthsSince(unixSeconds: number): number {
  const elapsedSeconds = Math.max(0, Math.round(CGT.now().toSeconds()) - unixSeconds);
  const elapsedDays = elapsedSeconds / (60 * 60 * 24);
  return elapsedDays / MONTH_IN_DAYS;
}

export function computeUpdatedStability(
  originalStability: string | null | undefined,
  lastUpdatedAt: number | null,
): { status: StabilityStatus; shouldBeExpired: boolean; elapsedMonths: number | null } {
  const normalized = normalizeStability(originalStability);
  if (!normalized || !lastUpdatedAt) {
    return {
      status: "unknown",
      shouldBeExpired: false,
      elapsedMonths: null,
    };
  }

  const elapsedMonths = getElapsedMonthsSince(lastUpdatedAt);
  const remainingMaxMonths = STABILITY_MAX_MONTHS[normalized] - elapsedMonths;
  if (remainingMaxMonths <= 0) {
    return {
      status: "expired",
      shouldBeExpired: true,
      elapsedMonths,
    };
  }

  const status: StabilityStatus =
    remainingMaxMonths < 1
      ? "volatile"
      : remainingMaxMonths < 3
        ? "unsteady"
        : remainingMaxMonths < 6
          ? "unsettled"
          : "stable";

  return {
    status,
    shouldBeExpired: false,
    elapsedMonths,
  };
}
